import { useState, useEffect } from 'react';
import { PopUpType } from '../../common/components/PopUp';
import { BiSortAlt2 } from 'react-icons/bi';
import { ContextMenuItem } from '../../common/components/ContextMenu';
import { useApi } from '../../api/useApi';
import { DeceasedTableItem, DeceasedTableParams } from '../../api/protocolsApi';
import { useNavigate } from 'react-router';
import { useReferences } from '../../helpers/context_manager/useReferences';
import { toOptions } from '../../helpers/toOptions';

type ContextMenuState = {
  isOpen: boolean;
  x: number;
  y: number;
  protocolId: number;
  is_locked?: boolean;
  is_deactivated?: boolean;
};

type Option = {
  value: string | number;
  label: string;
};

export type Options = {
  employees: Option[];
  years: Option[];
};

export const useProtocolList = () => {
  const navigate = useNavigate();
  const api = useApi();
  const { references, loading: referencesLoading } = useReferences();
  const [count, setCount] = useState<number>(0);
  const [data, setData] = useState<DeceasedTableItem[] | null>(null);
  const [years, setYears] = useState<Option[]>([]);
  const [message, setMessage] = useState<{ type: PopUpType; message: string } | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<DeceasedTableParams>({});
  const [ordering, setOrdering] = useState<string>('-id');
  const [limit, setLimit] = useState<number>(20);
  const [offset, setOffset] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    protocolId: 0
  });

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 11 }, (_, i) => {
      const year = currentYear - i;
      return { value: year, label: year.toString() };
    });
    setYears(yearOptions);
  }, []);

  useEffect(() => {
    loadData();
  }, [offset, limit, filterState]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.protocols.getDeceasedTable({
        limit,
        offset,
        ...filterState
      });
      setData(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      setMessage({
        type: 'error',
        message: 'Chyba při načítaní dat'
      });
      console.error('Error loading protocols:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrdering = (orderingBy: string) => {
    const newOrdering = ordering === orderingBy ? `-${orderingBy}` : orderingBy;
    setOrdering(newOrdering);
    localStorage.setItem('protocol-list-ordering', newOrdering);
    setFilterState((prev) => ({ ...prev, ordering: newOrdering }));
  };

  const handleFilterSubmit = (filterData: DeceasedTableParams) => {
    setFilterState(filterData);
    setOffset(0);
    setPage(1);
  };

  const handleContextMenu = (event: React.MouseEvent, protocolId: number, is_locked?: boolean, is_deactivated?: boolean) => {
    event.preventDefault();
    setContextMenu({
      isOpen: true,
      x: event.clientX,
      y: event.clientY,
      protocolId,
      is_locked,
      is_deactivated
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      x: 0,
      y: 0,
      protocolId: 0,
      is_locked: false,
      is_deactivated: false
    });
  };

  const handleToggleLock = async (id: number, is_locked?: boolean, is_deactivated?: boolean) => {
    if (!id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }
    if(!is_locked &&is_deactivated) {
      setMessage({ type: 'info', message: 'Protokol je deaktivován a nelze jej zamknout.' });
      return;
    }

    try {
      const response = await (is_locked ? api.protocols.unlock(id) : api.protocols.lock(id));
      if (response.status === 200) {
        setMessage({
          type: 'success',
          message: `Protokol byl ${is_locked ? 'odemčen' : 'uzamčen'}.`
        });
        loadData();
      }
    } catch (error: any) {
      if(error.status === 403) {
        setMessage({ type: 'info', message: `Nemáte oprávnění k ${is_locked ? 'odemčení' : 'uzamčení'} protokolu.` });
        return;
      }
      console.error(`Chyba při ${is_locked ? 'odemčení' : 'uzamčení'} protokolu:`, error);
      let errorMessage = `Nepodařilo se ${is_locked ? 'odemknout' : 'uzamknout'} protokol.`;
      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const handlePrint = async (id: number) => {
    if (!id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.print(id);
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
        setMessage({ type: 'success', message: 'Protokol byl odeslán k tisku.' });
      }
    } catch (error: any) {
      console.error('Chyba při tisku protokolu:', error);
      let errorMessage = 'Nepodařilo se vytisknout protokol.';
      setMessage({ type: 'error', message: errorMessage });
    }
  };
  
  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Přejít na osobní údaje',
      onClick: () => {
        navigate(`/protocol/${contextMenu.protocolId}/patient`);
      }
    },
    {
      label: 'Přejít na protokol',
      onClick: () => {
        navigate(`/protocol/${contextMenu.protocolId}/protocol`);
      }
    },
    {
      label: 'Přejít na žádanku',
      onClick: () => {
        navigate(`/protocol/${contextMenu.protocolId}/requests`);
      }
    },
    {
      label: 'Přejít na výsledky',
      onClick: () => {
        navigate(`/protocol/${contextMenu.protocolId}/results`);
      }
    },
    {
      label: contextMenu.is_locked ? 'Odemknout' : 'Zamknout',
      onClick: () => {
        handleToggleLock(contextMenu.protocolId, contextMenu.is_locked, contextMenu.is_deactivated);
      }
    },
    {
      label: 'Tisk protokolu',
      onClick: () => {
        handlePrint(contextMenu.protocolId);
      }
    }
  ];

  const handlePagination = (newOffset: number, newPage: number) => {
    setOffset(newOffset);
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setOffset(0);
    setPage(1);
  };

  const columns = [
    {
      label: 'Jméno',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('name')} />
        </span>
      )
    },
    {
      label: 'Protokol',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('protocol')} />
        </span>
      )
    },
    {
      label: 'Pitvající 1',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('doctor')} />
        </span>
      )
    },
    {
      label: 'Pitvající 2',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('doctor2')} />
        </span>
      )
    },
    {
      label: 'Vykázán',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('sentKDavka')} />
        </span>
      )
    },
    {
      label: 'Data o pitvě NRPATV',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('sentNRPATV')} />
        </span>
      )
    },
    {
      label: 'Aktivní žádanka',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('activeRequest')} />
        </span>
      )
    },
    {
      label: 'Tituly',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('title')} />
        </span>
      )
    },
    {
      label: 'Rodné číslo',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('birthNumber')} />
        </span>
      )
    },
    {
      label: 'Pojišťovna',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('insurance')} />
        </span>
      )
    },
    {
      label: 'Datum narození',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('birthDate')} />
        </span>
      )
    },
    {
      label: 'Datum úmrtí',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('deathDate')} />
        </span>
      )
    },
    {
      label: 'Věk',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('age')} />
        </span>
      )
    },
    {
      label: 'Bydliště',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('residence')} />
        </span>
      )
    },
    {
      label: 'Kontakt',
      order: (
        <span>
          <BiSortAlt2 onClick={() => handleOrdering('contact')} />
        </span>
      )
    },
    {
      label: 'Poznámka'
    }
  ];

  const togglePopup = () => {
    setMessage(undefined);
  };

  return {
    columns,
    message,
    data,
    loading: loading || referencesLoading,
    count,
    options: {
      employees: toOptions(references.employees),
      years: years
    },
    contextMenu,
    contextMenuItems,
    limit,
    offset,
    page,
    navigate,
    togglePopup,
    handleOrdering,
    handleFilterSubmit,
    handlePagination,
    handleLimitChange,
    handleContextMenu,
    handleCloseContextMenu
  };
};
